import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Users, Plus, Edit2, Trash2, X, Loader2, 
  UserPlus, UserMinus, GraduationCap, Clock, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const COURSE_TYPES = ['Conversation', 'Grammar', 'Business English', 'IELTS Prep', 'TOEFL Prep', 'General'];

export const AdminGroups = () => {
  const { user, isStrictAdmin } = useAuth();
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    level: '',
    max_students: 10,
    course_type: '',
    schedule_info: '',
    teacher_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, studentsRes, teachersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/groups?page_size=200`),
        axios.get(`${API_URL}/api/admin/students?page_size=200`),
        axios.get(`${API_URL}/api/admin/teachers?page_size=200`)
      ]);
      setGroups(groupsRes.data.data ?? groupsRes.data);
      const studentsList = studentsRes.data.data ?? studentsRes.data;
      setStudents(studentsList.filter(s => s.status === 'approved'));
      setTeachers(teachersRes.data.data ?? teachersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name || '',
        description: group.description || '',
        level: group.level || '',
        max_students: group.max_students || 10,
        course_type: group.course_type || '',
        schedule_info: group.schedule_info || '',
        teacher_id: group.teacher_id || user?.id || ''
      });
    } else {
      setEditingGroup(null);
      setFormData({
        name: '',
        description: '',
        level: '',
        max_students: 10,
        course_type: '',
        schedule_info: '',
        teacher_id: teachers[0]?.id || user?.id || ''
      });
    }
    setModalOpen(true);
  };

  const openManageModal = async (group) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/groups/${group.id}`);
      setSelectedGroup(response.data);
      setManageModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch group details:', err);
      toast.error('Failed to load group details');
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingGroup(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a group name');
      return;
    }

    setSaving(true);
    try {
      if (editingGroup) {
        await axios.put(`${API_URL}/api/admin/groups/${editingGroup.id}`, formData);
        toast.success('Group updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/groups`, formData);
        toast.success('Group created successfully');
      }
      closeModal();
      fetchData();
    } catch (err) {
      console.error('Failed to save group:', err);
      toast.error(err.response?.data?.detail || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;

    setDeleting(groupId);
    try {
      await axios.delete(`${API_URL}/api/admin/groups/${groupId}`);
      toast.success('Group deleted');
      fetchData();
    } catch (err) {
      console.error('Failed to delete group:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete group');
    } finally {
      setDeleting(null);
    }
  };

  const addStudentToGroup = async (studentId) => {
    if (!selectedGroup) return;
    try {
      await axios.post(`${API_URL}/api/admin/groups/${selectedGroup.id}/students/${studentId}`);
      toast.success('Student added to group');
      // Refresh group data
      const response = await axios.get(`${API_URL}/api/admin/groups/${selectedGroup.id}`);
      setSelectedGroup(response.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add student');
    }
  };

  const removeStudentFromGroup = async (studentId) => {
    if (!selectedGroup) return;
    try {
      await axios.delete(`${API_URL}/api/admin/groups/${selectedGroup.id}/students/${studentId}`);
      toast.success('Student removed from group');
      // Refresh group data
      const response = await axios.get(`${API_URL}/api/admin/groups/${selectedGroup.id}`);
      setSelectedGroup(response.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove student');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Learning Groups
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Manage student groups for class organization
          </p>
        </div>
        <button
          onClick={() => openModal()}
          data-testid="create-group-btn"
          className="flex items-center gap-2 px-5 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium"
        >
          <Plus size={20} />
          New Group
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">{groups.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Total Groups</p>
        </div>
        <div className="bg-[#4CAF50]/5 rounded-xl border border-[#4CAF50]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#4CAF50]">
            {groups.filter(g => g.is_active !== false).length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Active</p>
        </div>
        <div className="bg-[#1A2E6E]/5 rounded-xl border border-[#1A2E6E]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">
            {groups.reduce((sum, g) => sum + (g.student_count || 0), 0)}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Total Students</p>
        </div>
        <div className="bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#F5A623]">
            {students.filter(s => !groups.some(g => g.student_ids?.includes(s.id))).length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Unassigned</p>
        </div>
      </div>

      {/* Groups Grid */}
      {groups.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group, index) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="p-5 border-b border-[#E8EAF0]">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#1A2E6E]/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#1A2E6E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1A2E6E]">{group.name}</h3>
                      {group.level && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] font-medium">
                          {group.level}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openModal(group)}
                      className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      disabled={deleting === group.id}
                      className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                    >
                      {deleting === group.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
                
                {group.description && (
                  <p className="text-sm text-[#1A2E6E]/60 line-clamp-2">{group.description}</p>
                )}
              </div>

              {/* Details */}
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#1A2E6E]/60 flex items-center gap-1.5">
                    <GraduationCap size={14} />
                    Students
                  </span>
                  <span className="font-medium text-[#1A2E6E]">
                    {group.student_count || 0} / {group.max_students}
                  </span>
                </div>
                
                {group.course_type && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#1A2E6E]/60">Course</span>
                    <span className="font-medium text-[#1A2E6E]">{group.course_type}</span>
                  </div>
                )}
                
                {group.schedule_info && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#1A2E6E]/60 flex items-center gap-1.5">
                      <Clock size={14} />
                      Schedule
                    </span>
                    <span className="font-medium text-[#1A2E6E]">{group.schedule_info}</span>
                  </div>
                )}

                {/* Progress bar */}
                <div className="pt-2">
                  <div className="h-2 bg-[#E8EAF0] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#4CAF50] rounded-full transition-all"
                      style={{ width: `${((group.student_count || 0) / group.max_students) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-4 bg-[#FAFAF7] border-t border-[#E8EAF0]">
                <button
                  onClick={() => openManageModal(group)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-[#1A2E6E] hover:bg-[#1A2E6E]/10 rounded-lg transition-colors"
                >
                  <UserPlus size={16} />
                  Manage Students
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <Users className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#1A2E6E] mb-2">No Groups Yet</h3>
          <p className="text-[#1A2E6E]/60 mb-4">
            Create your first learning group to organize students
          </p>
          <button
            onClick={() => openModal()}
            className="px-5 py-2.5 bg-[#D72B2B] text-white rounded-lg hover:bg-[#B71C1C] transition-colors font-medium"
          >
            Create Group
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-xl font-bold text-[#1A2E6E]">
                  {editingGroup ? 'Edit Group' : 'New Group'}
                </h2>
                <button onClick={closeModal} className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Group Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Intermediate Conversation"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    required
                  />
                </div>

                {isStrictAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                      Teacher <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.teacher_id}
                      onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                      required
                    >
                      <option value="">Select a teacher</option>
                      {teachers.map(teacher => (
                        <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Group description..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Level</label>
                    <select
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                    >
                      <option value="">Select level</option>
                      {LEVELS.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Max Students</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={formData.max_students}
                      onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) || 10 })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Course Type</label>
                  <select
                    value={formData.course_type}
                    onChange={(e) => setFormData({ ...formData, course_type: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                  >
                    <option value="">Select type</option>
                    {COURSE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Schedule Info</label>
                  <input
                    type="text"
                    value={formData.schedule_info}
                    onChange={(e) => setFormData({ ...formData, schedule_info: e.target.value })}
                    placeholder="e.g., Mon/Wed 7pm-8pm"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={closeModal}
                    className="flex-1 py-3 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 font-medium">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving}
                    className="flex-1 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                    {editingGroup ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manage Students Modal */}
      <AnimatePresence>
        {manageModalOpen && selectedGroup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setManageModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-xl font-bold text-[#1A2E6E]">{selectedGroup.name}</h2>
                  <p className="text-sm text-[#1A2E6E]/60">
                    {selectedGroup.students?.length || 0} / {selectedGroup.max_students} students
                  </p>
                </div>
                <button onClick={() => setManageModalOpen(false)} className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Current Members */}
                  <div>
                    <h3 className="font-semibold text-[#1A2E6E] mb-3 flex items-center gap-2">
                      <Users size={16} />
                      Current Members ({selectedGroup.students?.length || 0})
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedGroup.students?.length > 0 ? (
                        selectedGroup.students.map(student => (
                          <div key={student.id} className="flex items-center justify-between p-3 bg-[#FAFAF7] rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center text-sm font-medium text-[#1A2E6E]">
                                {student.name?.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#1A2E6E]">{student.name}</p>
                                <p className="text-xs text-[#1A2E6E]/60">{student.email}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => removeStudentFromGroup(student.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <UserMinus size={16} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-[#1A2E6E]/50 text-center py-4">No students in this group</p>
                      )}
                    </div>
                  </div>

                  {/* Available Students */}
                  <div>
                    <h3 className="font-semibold text-[#1A2E6E] mb-3 flex items-center gap-2">
                      <UserPlus size={16} />
                      Available Students
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {students
                        .filter(s => !selectedGroup.student_ids?.includes(s.id))
                        .map(student => (
                          <div key={student.id} className="flex items-center justify-between p-3 bg-[#FAFAF7] rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#4CAF50]/10 flex items-center justify-center text-sm font-medium text-[#4CAF50]">
                                {student.name?.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#1A2E6E]">{student.name}</p>
                                <p className="text-xs text-[#1A2E6E]/60">{student.email}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => addStudentToGroup(student.id)}
                              disabled={selectedGroup.students?.length >= selectedGroup.max_students}
                              className="p-1.5 text-[#4CAF50] hover:bg-[#4CAF50]/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <UserPlus size={16} />
                            </button>
                          </div>
                        ))
                      }
                      {students.filter(s => !selectedGroup.student_ids?.includes(s.id)).length === 0 && (
                        <p className="text-sm text-[#1A2E6E]/50 text-center py-4">All students are assigned</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminGroups;
